import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  MapPin,
  Calendar,
  Settings,
  Tag,
  MessageCircle,
  UserPlus,
  UserMinus,
  Crown,
  MoreVertical,
  Check,
  X,
  Share2
} from 'lucide-react';
import { useGroupStore } from '../store/groupStore';
import { useAuthStore } from '../store/authStore';
import { groupService } from '../services/groupService';
import { premiumService } from '../services/premiumService';
import { Button, LoadingSpinner, ProfileAvatar, UserProfilePopover, NoticeContainer, CapacityNotice, ConfirmationDialog, ShareTriggerButton } from '../components/ui';
import { PageHeader } from '../components/layout';
import { toast } from '../components/ui/Toaster';
import { EditGroupModal } from '../components/modals';
import UpgradeModal from '../components/modals/UpgradeModal';
import { UpgradeRequiredError } from '../types/premiumTypes';
import { useNotice, noticeHelpers } from '../contexts/NoticeContext';
import { ShareMenu } from '../components/sharing';
// Backend notifications handled automatically

const GroupDetailPage: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading, isAuthenticated: _isAuthenticated } = useAuthStore();
  const { addNotice } = useNotice();
  const {
    currentGroup,
    groupMembers,
    isLoading,
    isLoadingMembers,
    error,
    setCurrentGroup,
    setGroupMembers,
    setLoading,
    setLoadingMembers,
    setError,
    clearError,
    updateGroup,
    removeGroup: _removeGroup,
    removeGroupMember,
  } = useGroupStore();

  const [showMemberActions, setShowMemberActions] = useState<number | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [isLeaving, _setIsLeaving] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [_upgradeError, _setUpgradeError] = useState<UpgradeRequiredError | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  // Capacity notice state (following GroupsPage pattern)
  const [showCapacityNotice, setShowCapacityNotice] = useState(false);
  const [capacityMessage, setCapacityMessage] = useState('');
  // Confirmation dialogs
  const [confirmationDialog, setConfirmationDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    type: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
    loading?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: '',
    type: 'warning',
    onConfirm: () => {},
    loading: false,
  });

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMobileMenu && !(event.target as Element).closest('.relative')) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);

  const numericGroupId = groupId ? parseInt(groupId) : null;
  const members = numericGroupId ? groupMembers[numericGroupId] || [] : [];
  const isOwner = currentGroup?.ownerId === user?.id;
  const currentMember = user ? members.find(member => member.userId === user.id) : null;
  const memberStatus = currentGroup?.currentUserMemberStatus || currentMember?.status;
  const isMember = memberStatus === 'ACCEPTED';
  const isPending = memberStatus === 'PENDING';
  const isRejected = memberStatus === 'REJECTED';

  useEffect(() => {
    if (numericGroupId) {
      loadGroup();
      loadMembers();
    }
  }, [numericGroupId]);


  // Show notice for rejected users
  useEffect(() => {
    if (isRejected && currentGroup) {
      addNotice(noticeHelpers.groupRejectedCannotReapply(currentGroup.name));
    }
  }, [isRejected, currentGroup, addNotice]);

  // Tracks the most recently requested group id so a slow response for a group
  // the user already navigated away from cannot overwrite the current one.
  const loadGroupRequestRef = useRef<number | null>(null);

  const loadGroup = async () => {
    if (!numericGroupId) return;
    const requestedId = numericGroupId;
    loadGroupRequestRef.current = requestedId;

    try {
      setLoading(true);
      clearError();
      const group = await groupService.getGroupById(numericGroupId);
      // Ignore a response that a newer group selection has superseded.
      if (loadGroupRequestRef.current !== requestedId) return;
      setCurrentGroup(group);

      // Check group size limits after loading
      checkGroupSizeLimit();
    } catch (error: any) {
      if (loadGroupRequestRef.current !== requestedId) return;
      console.error('Failed to load group:', error);
      setError(error.message || 'Laden van groep mislukt');
      if (error.status === 404) {
        toast.error('Fout', 'Groep niet gevonden');
        navigate('/groups');
      }
    }
  };

  // Simple capacity check following GroupsPage pattern
  const checkGroupSizeLimit = async () => {
    if (!numericGroupId) return;

    try {
      const result = await premiumService.canExpandGroup(numericGroupId);

      // Handle undefined result (API failure)
      if (!result) {
        setShowCapacityNotice(false);
        return;
      }

      // Check if group needs capacity upgrade
      if (!result.canExpand && result.requiresUpgrade) {
        setShowCapacityNotice(true);
        setCapacityMessage(result.reason || 'Je groep zit vol. Upgrade voor €4,95 voor 5 extra plekken! (Max: 50)');
      } else {
        setShowCapacityNotice(false);
      }
    } catch (error) {
      console.error('❌ [GroupDetailPage] Failed to check group size limit:', error);
      // On error, default to not showing notice to avoid blocking users
      setShowCapacityNotice(false);
    }
  };

  const loadMembers = async () => {
    if (!numericGroupId) return;

    try {
      setLoadingMembers(true);
      const response = await groupService.getGroupMembers(numericGroupId);
      setGroupMembers(numericGroupId, response.members);
    } catch (error: any) {
      console.error('Failed to load members:', error);
    }
  };

  const handleSizeUpgradeClick = () => {
    _setUpgradeError(new UpgradeRequiredError({
      message: 'Group capacity upgrade needed',
      requiresUpgrade: true,
      upgradeType: 'EXTENDED_GROUP_SIZE',
      price: 4.95,
    }));
    setShowUpgradeModal(true);
  };

  const handleUpgradeComplete = () => {
    setShowUpgradeModal(false);
    // Reload group data to get updated max_members and refresh capacity check
    loadGroup();
  };

  const handleJoinGroup = async () => {
    if (!numericGroupId) return;

    try {
      setIsJoining(true);
      await groupService.joinGroup(numericGroupId);

      // Reload group data to get updated currentUserMemberStatus
      await loadGroup();

      // Reload members to get updated list
      await loadMembers();

      toast.success('Succes', 'Aanvraag om lid te worden succesvol verzonden!');

      // Add persistent notice about group application
      // Use a timeout to ensure state has updated
      setTimeout(() => {
        if (currentGroup) {
          addNotice(noticeHelpers.groupApplicationSent(currentGroup.name));
        }
      }, 100);
    } catch (error: any) {
      console.error('Failed to join group:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Aansluiten bij groep mislukt';
      toast.error('Fout', errorMessage);
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveGroup = async () => {
    if (!numericGroupId || !currentMember) return;

    // Show confirmation dialog based on user role
    if (isOwner) {
      const acceptedMembersCount = acceptedMembers.filter(m => m.userId !== user?.id).length;

      if (acceptedMembersCount > 0) {
        showConfirmation(
          'Groep verlaten',
          `Als eigenaar verlaat je de groep, wordt het eigendom automatisch overgedragen aan het langst bestaande lid.\n\nWeet je zeker dat je de groep wilt verlaten?`,
          'Groep verlaten',
          () => executeLeaveGroup(),
          'warning'
        );
      } else {
        showConfirmation(
          'Groep verlaten',
          `Je bent de enige in de groep. Als je vertrekt wordt de groep automatisch verwijderd.\n\nWeet je zeker dat je wilt vertrekken?`,
          'Groep verlaten',
          () => executeLeaveGroup(),
          'warning'
        );
      }
    } else {
      // Regular member confirmation
      showConfirmation(
        'Groep verlaten',
        `Weet je zeker dat je de groep "${currentGroup?.name}" wilt verlaten?`,
        'Groep verlaten',
        () => executeLeaveGroup(),
        'warning'
      );
    }
  };

  const executeLeaveGroup = async () => {
    if (!numericGroupId || !currentMember) return;

    try {
      setConfirmationLoading(true);
      await groupService.leaveGroup(numericGroupId);

      // Update group member count
      if (currentGroup) {
        updateGroup(numericGroupId, {
          currentMembers: currentGroup.currentMembers - 1
        });
      }

      // Remove member from the list
      removeGroupMember(numericGroupId, currentMember.userId);

      toast.success('Succes', 'Groep succesvol verlaten');
      hideConfirmation();
      navigate('/groups');
    } catch (error: any) {
      console.error('Failed to leave group:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Verlaten van groep mislukt';
      toast.error('Fout', errorMessage);
      setConfirmationLoading(false);
    }
  };

  // Helper function to show confirmation dialog
  const showConfirmation = (
    title: string,
    message: string,
    confirmText: string,
    onConfirm: () => void,
    type: 'danger' | 'warning' | 'info' = 'warning'
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      type,
      onConfirm,
      loading: false,
    });
  };

  const hideConfirmation = () => {
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };

  const setConfirmationLoading = (loading: boolean) => {
    setConfirmationDialog(prev => ({ ...prev, loading }));
  };

  const handleMemberAction = async (memberId: number, action: 'accept' | 'reject' | 'remove' | 'transfer') => {
    if (!numericGroupId) return;

    const member = members.find(m => m.userId === memberId);
    if (!member) return;

    // Handle actions that need confirmation
    if (action === 'remove') {
      showConfirmation(
        'Lid verwijderen',
        `Weet je zeker dat je ${member.user.username} uit de groep wilt verwijderen?`,
        'Verwijderen',
        () => executeRemoveMember(memberId),
        'danger'
      );
      return;
    }

    if (action === 'transfer') {
      showConfirmation(
        'Eigendom overdragen',
        `Weet je zeker dat je het eigendom van de groep wilt overdragen aan ${member.user.username}?\n\nJe wordt gewoon lid van de groep en ${member.user.username} wordt de nieuwe eigenaar.`,
        'Eigendom overdragen',
        () => executeTransferOwnership(memberId),
        'warning'
      );
      return;
    }

    // Handle direct actions
    try {
      // Backend will automatically send notification to member

      switch (action) {
        case 'accept':
          await groupService.acceptMember(numericGroupId, memberId);
          // Reload members to get updated list
          loadMembers();
          toast.success('Succes', 'Lid geaccepteerd');

          // ✅ Notifications are now sent via backend API and delivered in real-time via Socket.IO
          break;

        case 'reject':
          await groupService.rejectMember(numericGroupId, memberId);
          // Reload members to get updated list
          loadMembers();
          toast.success('Succes', 'Lid afgewezen');

          // ✅ Notifications are now sent via backend API and delivered in real-time via Socket.IO
          break;
      }

      setShowMemberActions(null);
    } catch (error: any) {
      console.error(`Failed to ${action} member:`, error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${action} member`;
      toast.error('Fout', errorMessage);
    }
  };

  const executeRemoveMember = async (memberId: number) => {
    if (!numericGroupId) return;

    try {
      setConfirmationLoading(true);
      await groupService.removeMember(numericGroupId, memberId);

      // Reload members to get updated list
      loadMembers();

      // Update group member count
      if (currentGroup) {
        updateGroup(numericGroupId, {
          currentMembers: currentGroup.currentMembers - 1
        });
      }

      toast.success('Succes', 'Lid verwijderd');
      hideConfirmation();
      setShowMemberActions(null);
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove member';
      toast.error('Fout', errorMessage);
      setConfirmationLoading(false);
    }
  };

  const executeTransferOwnership = async (newOwnerId: number) => {
    if (!numericGroupId) return;

    try {
      setConfirmationLoading(true);
      await groupService.transferOwnership(numericGroupId, newOwnerId);

      // Reload group to get updated ownership
      await loadGroup();

      // Reload members to get updated list
      loadMembers();

      toast.success('Succes', 'Eigendom succesvol overgedragen');
      hideConfirmation();
      setShowMemberActions(null);
    } catch (error: any) {
      console.error('Failed to transfer ownership:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to transfer ownership';
      toast.error('Fout', errorMessage);
      setConfirmationLoading(false);
    }
  };


  // Handle group update from modal
  const handleGroupUpdated = (updatedGroup: any) => {
    setCurrentGroup(updatedGroup);
    setShowEditGroup(false);
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const acceptedMembers = members.filter(member => member.status === 'ACCEPTED');
  const pendingMembers = members.filter(member => member.status === 'PENDING');

  if (isLoading || isAuthLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Laden..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="rounded-field border border-neon-pink/40 bg-neon-pink/10 p-4">
          <div className="text-sm text-neon-pink-soft">{error}</div>
        </div>
      </div>
    );
  }

  if (!currentGroup) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <h2 className="text-lg font-bold text-fg-bright mb-2">Groep niet gevonden</h2>
        <p className="text-fg-muted">De groep die je zoekt bestaat niet of is verwijderd.</p>
        <Link
          to="/groups"
          className="sf-btn-primary px-4 py-2.5 text-sm mt-4"
        >
          Terug naar groepen
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notice Container */}
      <NoticeContainer pageKey={`page:group_application_${currentGroup.name}`} />

      {/* Header */}
      <PageHeader
        title={currentGroup.name}
        subtitle={
          <div className="flex flex-col gap-1 text-sm text-fg-muted sm:flex-row sm:items-center sm:gap-4">
            {/* Event name */}
            {currentGroup.event && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-neon-pink" />
                <span className="font-medium">{currentGroup.event.name}</span>
              </div>
            )}
            {/* Member count */}
            <div className="flex items-center gap-1.5">
              <Users className="h-4 w-4 text-neon-cyan" />
              <span>{currentGroup.currentMembers}/{currentGroup.maxMembers} leden</span>
            </div>
          </div>
        }
        breadcrumb={
          <button
            onClick={() => navigate('/groups')}
            className="flex items-center gap-2 text-sm text-fg-muted transition-colors hover:text-fg-soft"
          >
            <ArrowLeft className="h-4 w-4" />
            Terug naar groepen
          </button>
        }
        actions={
          (isOwner || isMember) ? (
            <>
              {/* Mobile - horizontal icon buttons */}
              <div className="sm:hidden flex items-center space-x-2">
                {isOwner && (
                  <>
                    <Link to={`/groups/${groupId}/manage`}>
                      <button
                        className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-btn border border-white/10 bg-white/5 p-2 text-fg-card transition-colors hover:bg-white/10"
                        title="Verzoeken beheren"
                      >
                        <Users className="h-5 w-5" />
                      </button>
                    </Link>
                    <button
                      onClick={() => setShowEditGroup(true)}
                      className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-btn border border-white/10 bg-white/5 p-2 text-fg-card transition-colors hover:bg-white/10"
                      title="Groep bewerken"
                    >
                      <Settings className="h-5 w-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={() => setShowShareMenu(true)}
                  className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-btn border border-white/10 bg-white/5 p-2 text-fg-card transition-colors hover:bg-white/10"
                  title="Groep delen"
                >
                  <Share2 className="h-5 w-5" />
                </button>
                <button
                  onClick={handleLeaveGroup}
                  className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-btn border border-neon-pink/40 bg-transparent p-2 text-neon-pink-soft transition-colors hover:bg-neon-pink/10"
                  title="Groep verlaten"
                >
                  <UserMinus className="h-5 w-5" />
                </button>
              </div>

              {/* Desktop buttons - horizontal layout */}
              <div className="hidden sm:flex sm:items-center sm:space-x-2">
                {isOwner && (
                  <>
                    <Link to={`/groups/${groupId}/manage`}>
                      <Button
                        variant="secondary"
                        size="md"
                        className="text-[13px]"
                      >
                        <Users className="h-4 w-4 mr-2" />
                        Verzoeken beheren
                      </Button>
                    </Link>
                    <Button
                      onClick={() => setShowEditGroup(true)}
                      variant="secondary"
                      size="md"
                      className="text-[13px]"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Bewerken
                    </Button>
                  </>
                )}
                <ShareTriggerButton
                  onClick={() => setShowShareMenu(true)}
                  variant="outline"
                  size="md"
                  className="text-[13px]"
                />
                <Button
                  onClick={handleLeaveGroup}
                  loading={isLeaving}
                  variant="danger"
                  size="md"
                  className="text-[13px]"
                >
                  <UserMinus className="h-4 w-4 mr-2" />
                  Verlaten
                </Button>
              </div>
            </>
          ) : undefined
        }
      />

      {/* Group Capacity Notice */}
      {showCapacityNotice && isOwner && (
        <CapacityNotice
          title="Wat een succes!"
          message={capacityMessage}
          onUpgradeClick={handleSizeUpgradeClick}
        />
      )}

      {/* Content grid */}
      <div className="grid grid-cols-1 gap-[22px] lg:grid-cols-[1.6fr_1fr]">
        {/* Main Content */}
        <div className="space-y-5">
          {/* Chat Section (if member) */}
          {isMember && (
            <div className="sf-card-highlight flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-fg-bright">Groep chat</h2>
                <p className="mt-1 text-sm text-fg-body">
                  Verbind met je groepsleden — realtime.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link to={`/groups/${groupId}/chat`}>
                  <Button
                    size="lg"
                    className="w-full animate-neon-pulse whitespace-nowrap sm:w-auto"
                  >
                    <MessageCircle className="h-5 w-5 mr-2" />
                    Open chat
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Group Info Section */}
          <div className="sf-card p-6">
            <h3 className="mb-3 text-lg font-bold text-fg-bright">Over deze groep</h3>
            <p className="text-[15px] leading-relaxed text-fg-body">{currentGroup.description}</p>

            <div className="sf-divider my-5"></div>

            <div className="flex flex-wrap gap-x-9 gap-y-5">
              {/* Event Info */}
              {currentGroup.event && (
                <div>
                  <div className="sf-label-mono mb-2">Evenement</div>
                  <div className="flex items-center text-sm font-semibold text-fg-bright">
                    <Calendar className="h-4 w-4 mr-2 text-neon-cyan" />
                    <span>{currentGroup.event.name}</span>
                    <span className="mx-2 text-fg-meta">•</span>
                    <MapPin className="h-4 w-4 mr-1 text-neon-pink" />
                    <span>{currentGroup.event.location}</span>
                  </div>
                  <div className="mt-1 text-[13px] text-fg-muted">
                    {formatDate(currentGroup.event.startDate)} - {currentGroup.event.endDate ? formatDate(currentGroup.event.endDate) : ''}
                  </div>
                </div>
              )}

              {/* Owner */}
              {currentGroup.owner && (
                <div>
                  <div className="sf-label-mono mb-2">Groep eigenaar</div>
                  <UserProfilePopover
                    userId={currentGroup.owner.id}
                    placement="auto"
                    trigger={
                      <div className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-fg-bright transition-opacity hover:opacity-80">
                        <Crown className="h-4 w-4 text-neon-lime" />
                        <span>{currentGroup.owner.username}</span>
                      </div>
                    }
                  />
                </div>
              )}
            </div>

            {/* Tags */}
            {currentGroup.tags.length > 0 && (
              <div className="mt-5">
                <div className="sf-label-mono mb-2">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {currentGroup.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="sf-badge-cyan"
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          {/* Join/Leave Actions */}
          {user && !isOwner && !isMember && !isPending && !isRejected && currentGroup.currentMembers < currentGroup.maxMembers && (
            <div className="space-y-3">
              <Button
                onClick={handleJoinGroup}
                loading={isJoining}
                size="md"
                className="w-full animate-neon-pulse"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Groep joinen
              </Button>
              <ShareTriggerButton
                onClick={() => setShowShareMenu(true)}
                variant="outline"
                size="md"
                className="w-full"
              />
            </div>
          )}

          {/* Full group: non-member (not pending/rejected) can't join — explain
              why and point them to the other groups for this event. */}
          {user && !isOwner && !isMember && !isPending && !isRejected && currentGroup.currentMembers >= currentGroup.maxMembers && (
            <div className="sf-item p-4 text-center space-y-3">
              <div className="flex justify-center">
                <span className="sf-badge-pink">Groep is vol</span>
              </div>
              <p className="text-sm text-fg-muted">
                Deze groep zit vol ({currentGroup.currentMembers}/{currentGroup.maxMembers} leden).
                Bekijk de andere groepen van dit evenement om er alsnog eentje te vinden.
              </p>
              <Link
                to={currentGroup.event?.slug ? `/events/${currentGroup.event.slug}` : '/groups'}
                className="block"
              >
                <Button variant="outline" size="md" className="w-full">
                  <Users className="h-4 w-4 mr-2" />
                  Bekijk andere groepen
                </Button>
              </Link>
              <ShareTriggerButton
                onClick={() => setShowShareMenu(true)}
                variant="ghost"
                size="md"
                className="w-full"
              />
            </div>
          )}

          {/* Share button for non-members when other buttons are not shown */}
          {user && !isOwner && !isMember && (isPending || isRejected) && (
            <div>
              <ShareTriggerButton
                onClick={() => setShowShareMenu(true)}
                variant="outline"
                size="md"
                className="w-full"
              />
            </div>
          )}

          {isPending && (
            <div className="sf-item p-4 text-center">
              <div className="mb-2 flex justify-center">
                <span className="sf-badge-cyan">Verzoek in behandeling</span>
              </div>
              <p className="text-sm text-fg-muted">
                Je verzoek om lid te worden wacht op goedkeuring
              </p>
            </div>
          )}

          {isRejected && (
            <div className="sf-item p-4 text-center">
              <div className="mb-2 flex justify-center">
                <span className="sf-badge-pink">Lidmaatschap geweigerd</span>
              </div>
              <p className="text-sm text-fg-muted">
                Je verzoek om lid te worden is afgewezen door de groepseigenaar
              </p>
            </div>
          )}


          {/* Members */}
          <div className="sf-card p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-fg-bright">
                Leden <span className="font-medium text-fg-meta">({acceptedMembers.length})</span>
              </h3>
              {isLoadingMembers && <LoadingSpinner size="sm" />}
            </div>

            <div className="space-y-2.5">
              {acceptedMembers.map((member) => (
                <div key={member.userId} className="sf-item flex items-center justify-between p-3">
                  <UserProfilePopover
                    userId={member.userId}
                    placement="auto"
                    trigger={
                      <div className="flex flex-1 cursor-pointer items-center gap-3 transition-opacity hover:opacity-80">
                        <ProfileAvatar
                          user={member.user}
                          size="sm"
                        />
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold text-fg-bright">
                              {member.user.username}
                            </span>
                            {member.userId === currentGroup.ownerId && (
                              <Crown className="h-3.5 w-3.5 text-neon-lime" />
                            )}
                          </div>
                          <span className="text-xs text-fg-muted">
                            Lid geworden {formatDate(member.joinedAt)}
                          </span>
                        </div>
                      </div>
                    }
                  />

                  {isOwner && member.userId !== user?.id && (
                    <div className="relative">
                      <button
                        onClick={() => setShowMemberActions(
                          showMemberActions === member.userId ? null : member.userId
                        )}
                        className="p-1 text-fg-faint transition-colors hover:text-fg-soft"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </button>

                      {showMemberActions === member.userId && (
                        <div className="absolute right-0 z-10 mt-2 w-48 overflow-hidden rounded-item border border-white/[.08] bg-surface shadow-panel">
                          <button
                            onClick={() => handleMemberAction(member.userId, 'transfer')}
                            className="block w-full px-4 py-2.5 text-left text-sm font-medium text-neon-cyan hover:bg-white/5"
                          >
                            Maak leider
                          </button>
                          <button
                            onClick={() => handleMemberAction(member.userId, 'remove')}
                            className="block w-full px-4 py-2.5 text-left text-sm font-medium text-neon-pink-soft hover:bg-neon-pink/10"
                          >
                            Verwijder uit groep
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pending Members (Owner Only) */}
            {isOwner && pendingMembers.length > 0 && (
              <div className="mt-6 border-t border-white/[.08] pt-4">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="text-[15px] font-bold text-fg-bright">
                    Wachtende verzoeken <span className="font-medium text-fg-meta">({pendingMembers.length})</span>
                  </h4>
                  <Link
                    to={`/groups/${groupId}/manage`}
                    className="text-xs font-semibold text-neon-cyan transition-opacity hover:opacity-80"
                  >
                    Alles beheren
                  </Link>
                </div>
                <div className="space-y-2.5">
                  {pendingMembers.map((member) => (
                    <div key={member.userId} className="sf-item flex items-center justify-between p-3">
                      <UserProfilePopover
                        userId={member.userId}
                        placement="auto"
                        trigger={
                          <div className="flex flex-1 cursor-pointer items-center gap-3 transition-opacity hover:opacity-80">
                            <ProfileAvatar
                              user={member.user}
                              size="sm"
                            />
                            <div>
                              <span className="text-sm font-semibold text-fg-bright">
                                {member.user.username}
                              </span>
                              <div className="text-xs text-fg-muted">
                                Verzoek {formatDate(member.joinedAt)}
                              </div>
                            </div>
                          </div>
                        }
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleMemberAction(member.userId, 'accept')}
                          className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] bg-accept-gradient transition-opacity hover:opacity-90"
                          title="Accepteren"
                        >
                          <Check className="h-4 w-4 text-ink" strokeWidth={2.6} />
                        </button>
                        <button
                          onClick={() => handleMemberAction(member.userId, 'reject')}
                          className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-white/[.12] bg-white/[.06] transition-colors hover:bg-white/10"
                          title="Afwijzen"
                        >
                          <X className="h-4 w-4 text-neon-pink-soft" strokeWidth={2.4} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Group Modal */}
      <EditGroupModal
        isOpen={showEditGroup}
        onClose={() => setShowEditGroup(false)}
        group={currentGroup}
        onGroupUpdated={handleGroupUpdated}
      />

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          upgradeType="EXTENDED_GROUP_SIZE"
          context={{
            ...(numericGroupId && { groupId: numericGroupId }),
            ...(currentGroup?.name && { groupName: currentGroup.name }),
          }}
          onUpgradeComplete={handleUpgradeComplete}
        />
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={hideConfirmation}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText={confirmationDialog.confirmText}
        type={confirmationDialog.type}
        loading={confirmationDialog.loading || false}
      />

      {/* Share Menu */}
      {currentGroup && (
        <ShareMenu
          type="group"
          data={currentGroup}
          isOpen={showShareMenu}
          onClose={() => setShowShareMenu(false)}
        />
      )}
    </div>
  );
};

export default GroupDetailPage;